import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Modal,
  Fade,
  Backdrop,
  alpha,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { Editor } from '@monaco-editor/react';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import { toast } from 'react-toastify';
import { formatDateTime } from '../../common/utils';
import { languageIdMapping } from '../../common/constants';
import { fetchSubmissionDetail } from '../../hooks/useProblemSubmissions';

const ACCENT = '#137274';
const ACCENT_HOVER = '#0f5c5e';

const LANG_LABELS = {
  62: 'Java',
  71: 'Python',
  54: 'C++',
  63: 'JavaScript',
  51: 'C#',
};

const formatSubmissionResult = (text) =>
  String(text ?? '')
    .replace(/\n/g, '<br />')
    .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');

const getStatusTheme = (status, isDark) => {
  const s = String(status || '').toLowerCase();
  if (s === 'accepted') {
    return { color: '#1a7f37', bg: alpha('#1a7f37', isDark ? 0.2 : 0.1) };
  }
  if (s.includes('wrong')) {
    return { color: '#cf222e', bg: alpha('#cf222e', isDark ? 0.2 : 0.1) };
  }
  if (
    s.includes('compilation') ||
    s.includes('runtime') ||
    s.includes('time limit') ||
    s.includes('error')
  ) {
    return { color: '#d97706', bg: alpha('#d97706', isDark ? 0.2 : 0.1) };
  }
  return {
    color: isDark ? '#8b949e' : '#5f6b73',
    bg: alpha(isDark ? '#8b949e' : '#5f6b73', 0.12),
  };
};

const StatCard = ({ label, value, hint, isDark, border }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 100,
      p: 1.5,
      borderRadius: 2,
      border: `1px solid ${border}`,
      bgcolor: isDark ? '#0d1117' : '#f8fafb',
    }}
  >
    <Typography
      variant="caption"
      sx={{ color: isDark ? '#8b949e' : '#5f6b73', fontWeight: 700, letterSpacing: '0.04em' }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.02em', mt: 0.25 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: isDark ? '#8b949e' : '#5f6b73', mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const ProblemSubmissionsTab = ({
  submissions = [],
  loading = false,
  error = null,
  onRefresh,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const total = submissions.length;
  const accepted = submissions.filter((s) => s.status === 'Accepted').length;
  const successRate = total ? Math.round((accepted / total) * 100) : 0;

  const openSubmission = async (submission) => {
    setSelected(submission);
    setModalOpen(true);
    setDetailError(null);

    if (submission.code) return;

    setDetailLoading(true);
    try {
      const full = await fetchSubmissionDetail(submission._id);
      setSelected(full);
    } catch (err) {
      setDetailError(
        err?.response?.data?.message || 'Could not load submission code.',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeSubmission = () => {
    setModalOpen(false);
    setSelected(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const copyCode = () => {
    if (!selected?.code) return;
    navigator.clipboard.writeText(selected.code).then(() => {
      toast.success('Code copied to clipboard');
    });
  };

  const editorTheme = isDark ? 'vs-dark' : 'light';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: '-0.02em' }}>
            Your submissions
          </Typography>
          <Typography variant="body2" sx={{ color: muted, mt: 0.25, fontSize: '0.82rem' }}>
            Past runs on this problem — newest first
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={loading}
            aria-label="refresh submissions"
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {loading && total === 0 && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: ACCENT }} />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            px: 2,
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${alpha('#cf222e', 0.35)}`,
            bgcolor: isDark ? '#3d1214' : '#fdecea',
          }}
        >
          <Typography variant="body2" sx={{ color: '#cf222e', mb: 1.5 }}>
            {error}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={onRefresh}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Try again
          </Button>
        </Box>
      )}

      {!loading && !error && total > 0 && (
        <Box sx={{ display: 'flex', gap: 1.25, mb: 2, flexWrap: 'wrap' }}>
          <StatCard label="ATTEMPTS" value={total} isDark={isDark} border={border} />
          <StatCard
            label="ACCEPTED"
            value={accepted}
            hint={accepted === 1 ? '1 solve' : accepted ? `${accepted} solves` : 'Not yet'}
            isDark={isDark}
            border={border}
          />
          <StatCard
            label="SUCCESS RATE"
            value={total ? `${successRate}%` : '—'}
            isDark={isDark}
            border={border}
          />
        </Box>
      )}

      {!loading && !error && total === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            px: 2,
            borderRadius: 2.5,
            border: `1px dashed ${border}`,
            bgcolor: isDark ? '#0d1117' : '#f8fafb',
          }}
        >
          <HistoryIcon sx={{ fontSize: 40, color: muted, mb: 1.5, opacity: 0.7 }} />
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No submissions yet</Typography>
          <Typography variant="body2" sx={{ color: muted, maxWidth: 280, mx: 'auto' }}>
            Run your solution against sample tests, then Submit to judge all test cases.
          </Typography>
        </Box>
      )}

      {!loading && !error && total > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {submissions.map((submission, index) => {
            const attempt = total - index;
            const { date, time } = formatDateTime(submission.submissionTime);
            const statusTheme = getStatusTheme(submission.status, isDark);
            const lang = LANG_LABELS[submission.languageId] || 'Code';

            return (
              <Box
                key={submission._id || index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  flexWrap: 'wrap',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  '&:hover': {
                    borderColor: alpha(ACCENT, 0.45),
                    boxShadow: isDark ? 'none' : '0 4px 14px rgba(15, 60, 70, 0.06)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.08),
                    color: ACCENT,
                    fontWeight: 800,
                    fontSize: '0.8rem',
                  }}
                >
                  #{attempt}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.35 }}>
                    <Chip
                      size="small"
                      label={submission.status}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        bgcolor: statusTheme.bg,
                        color: statusTheme.color,
                        border: `1px solid ${alpha(statusTheme.color, 0.25)}`,
                      }}
                    />
                    <Chip
                      size="small"
                      icon={<CodeOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                      label={lang}
                      sx={{
                        height: 22,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        bgcolor: isDark ? '#0d1117' : '#f6f8fa',
                        border: `1px solid ${border}`,
                      }}
                    />
                    {submission.pointsAwarded > 0 && (
                      <Chip
                        size="small"
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                        label={`+${submission.pointsAwarded} pts`}
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          bgcolor: alpha('#1a7f37', 0.12),
                          color: '#1a7f37',
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: muted, fontWeight: 550 }}>
                    {date} · {time}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => openSubmission(submission)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    borderColor: alpha(ACCENT, 0.5),
                    color: ACCENT,
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: ACCENT,
                      bgcolor: alpha(ACCENT, 0.06),
                    },
                  }}
                >
                  View
                </Button>
              </Box>
            );
          })}
        </Box>
      )}

      <Modal
        open={modalOpen}
        onClose={closeSubmission}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '94%', sm: '88%', md: 720 },
              maxWidth: 900,
              maxHeight: '88vh',
              bgcolor: surface,
              border: `1px solid ${border}`,
              boxShadow: 24,
              borderRadius: 2.5,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {selected && (
              <>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${border}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
                      Submission details
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                      <Chip
                        size="small"
                        label={selected.status}
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          ...(() => {
                            const t = getStatusTheme(selected.status, isDark);
                            return { bgcolor: t.bg, color: t.color };
                          })(),
                        }}
                      />
                      <Chip
                        size="small"
                        label={LANG_LABELS[selected.languageId] || 'Code'}
                        sx={{ height: 22, fontWeight: 600, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" sx={{ color: muted, alignSelf: 'center' }}>
                        {formatDateTime(selected.submissionTime).date}{' '}
                        {formatDateTime(selected.submissionTime).time}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={selected.code ? 'Copy code' : 'Code not loaded'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={copyCode}
                          disabled={!selected.code || detailLoading}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton size="small" onClick={closeSubmission} aria-label="close">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: muted, fontWeight: 700, letterSpacing: '0.04em', mb: 0.75, display: 'block' }}
                  >
                    CODE
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: 1.5,
                      border: `1px solid ${border}`,
                      overflow: 'hidden',
                      mb: 2,
                      minHeight: 240,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {detailLoading ? (
                      <CircularProgress size={28} sx={{ color: ACCENT }} />
                    ) : detailError ? (
                      <Typography variant="body2" sx={{ color: '#cf222e', p: 2, textAlign: 'center' }}>
                        {detailError}
                      </Typography>
                    ) : (
                      <Editor
                        height="240px"
                        language={languageIdMapping[selected.languageId] || 'java'}
                        value={selected.code || ''}
                        theme={editorTheme}
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 13,
                          automaticLayout: true,
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{ color: muted, fontWeight: 700, letterSpacing: '0.04em', mb: 0.75, display: 'block' }}
                  >
                    RESULT
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: `1px solid ${border}`,
                      bgcolor: (() => {
                        const t = getStatusTheme(selected.status, isDark);
                        if (selected.status === 'Accepted') {
                          return isDark ? '#0d2818' : '#f0faf4';
                        }
                        if (String(selected.status).toLowerCase().includes('wrong')) {
                          return isDark ? '#3d1214' : '#fdecea';
                        }
                        return isDark ? '#0d1117' : '#f6f8fa';
                      })(),
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 13,
                      lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatSubmissionResult(selected.submissionResult),
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: `1px solid ${border}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={closeSubmission}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: ACCENT,
                      '&:hover': { bgcolor: ACCENT_HOVER },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default ProblemSubmissionsTab;
